/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateTicketRequest } from '../models/create-ticket-request';
import { DetailedTicketResponse } from '../models/detailed-ticket-response';
import { GenericResponse } from '../models/generic-response';
import { GetTicketResponse } from '../models/get-ticket-response';
import { TicketAttachmentRequest } from '../models/ticket-attachment-request';
import { TicketResponse } from '../models/ticket-response';
import { TicketStatus } from '../models/ticket-status';

/**
 * ticket apis
 */
@Injectable({ providedIn: 'root' })
export class TicketService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllTickets()` */
  static readonly GetAllTicketsPath = '/api/v1/tickets';

  /**
   * Get list of tickets.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllTickets()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllTickets$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - status
       * - feedback
       *   - comment
       *   - status
       * - feedback type
       *   - type
       *   - name
       */
      search?: string;

      /**
       * The aircraft search in flight report
       */
      aircraft?: string;

      /**
       * The feedback type filter in ticket
       */
      feedbackTypeId?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetTicketResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TicketService.GetAllTicketsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('aircraft', params.aircraft, {});
      rb.query('feedbackTypeId', params.feedbackTypeId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetTicketResponse>;
        })
      );
  }

  /**
   * Get list of tickets.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllTickets$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllTickets(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - status
       * - feedback
       *   - comment
       *   - status
       * - feedback type
       *   - type
       *   - name
       */
      search?: string;

      /**
       * The aircraft search in flight report
       */
      aircraft?: string;

      /**
       * The feedback type filter in ticket
       */
      feedbackTypeId?: Array<number>;
    },
    context?: HttpContext
  ): Observable<GetTicketResponse> {
    return this.getAllTickets$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetTicketResponse>): GetTicketResponse => r.body
      )
    );
  }

  /** Path part for operation `createTicket()` */
  static readonly CreateTicketPath = '/api/v1/tickets';

  /**
   * Create new ticket.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTicket()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTicket$Response(
    params: {
      body: CreateTicketRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<TicketResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TicketService.CreateTicketPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<TicketResponse>;
        })
      );
  }

  /**
   * Create new ticket.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTicket$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTicket(
    params: {
      body: CreateTicketRequest;
    },
    context?: HttpContext
  ): Observable<TicketResponse> {
    return this.createTicket$Response(params, context).pipe(
      map((r: StrictHttpResponse<TicketResponse>): TicketResponse => r.body)
    );
  }

  /** Path part for operation `changeTicketStatus()` */
  static readonly ChangeTicketStatusPath = '/api/v1/tickets/{id}/{status}';

  /**
   * change status of ticket.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `changeTicketStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  changeTicketStatus$Response(
    params: {
      /**
       * The ticket's id that needs to be updated
       */
      id: number;

      /**
       * The status that needs to be fetched
       */
      status: TicketStatus;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TicketService.ChangeTicketStatusPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('status', params.status, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * change status of ticket.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `changeTicketStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  changeTicketStatus(
    params: {
      /**
       * The ticket's id that needs to be updated
       */
      id: number;

      /**
       * The status that needs to be fetched
       */
      status: TicketStatus;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.changeTicketStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `getTicketById()` */
  static readonly GetTicketByIdPath = '/api/v1/tickets/{id}';

  /**
   * fetch ticket by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTicketById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTicketById$Response(
    params: {
      /**
       * The ticket's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<DetailedTicketResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TicketService.GetTicketByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<DetailedTicketResponse>;
        })
      );
  }

  /**
   * fetch ticket by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTicketById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTicketById(
    params: {
      /**
       * The ticket's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<DetailedTicketResponse> {
    return this.getTicketById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<DetailedTicketResponse>
        ): DetailedTicketResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteTicket()` */
  static readonly DeleteTicketPath = '/api/v1/tickets/{id}';

  /**
   * Delete ticket  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteTicket()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTicket$Response(
    params: {
      /**
       * The ticket id that needs to be deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TicketService.DeleteTicketPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete ticket  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteTicket$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTicket(
    params: {
      /**
       * The ticket id that needs to be deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteTicket$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `uploadTicketAttachments()` */
  static readonly UploadTicketAttachmentsPath =
    '/api/v1/tickets/{id}/attachments';

  /**
   * Upload ticket Attachments.
   *
   * Upload image attachments for a ticket
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadTicketAttachments()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadTicketAttachments$Response(
    params: {
      /**
       * The ticket id to which the file is to be attached
       */
      id: number;
      body: TicketAttachmentRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TicketService.UploadTicketAttachmentsPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Upload ticket Attachments.
   *
   * Upload image attachments for a ticket
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadTicketAttachments$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadTicketAttachments(
    params: {
      /**
       * The ticket id to which the file is to be attached
       */
      id: number;
      body: TicketAttachmentRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.uploadTicketAttachments$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `downloadTicketAttachments()` */
  static readonly DownloadTicketAttachmentsPath =
    '/api/v1/tickets/{id}/attachments/{file-id}';

  /**
   * download ticket Attachments.
   *
   * download image attachments for a ticket
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadTicketAttachments()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadTicketAttachments$Response(
    params: {
      /**
       * The ticket id to which the file is to be downloaded
       */
      id: number;

      /**
       * The file id to be downloaded
       */
      'file-id': number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TicketService.DownloadTicketAttachmentsPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('file-id', params['file-id'], {});
    }

    return this.http
      .request(rb.build({ responseType: 'blob', accept: '*/*', context }))
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * download ticket Attachments.
   *
   * download image attachments for a ticket
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadTicketAttachments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadTicketAttachments(
    params: {
      /**
       * The ticket id to which the file is to be downloaded
       */
      id: number;

      /**
       * The file id to be downloaded
       */
      'file-id': number;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.downloadTicketAttachments$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }
}
